import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Input, StatusPill } from '@agms/ui'
import type { StatusTone } from '@agms/ui'
import { PageHeader, SurfaceCard } from '../components/ui'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorStatus } from '../hooks/useErrorHandler'
import {
  exportReport,
  fetchReport,
  type LastProcessedReportRow,
  type LegalNameReportRow,
  type NewAccountReportRow,
  type OwnerReportRow,
  type ReportEnvelope,
  type ReportId,
  type ReportParams,
  type StatusReportRow,
} from '../lib/reportsApi'

const REPORTS: Array<{ id: ReportId; label: string }> = [
  { id: 'owners', label: 'By owner' },
  { id: 'legal-names', label: 'By legal name' },
  { id: 'statuses', label: 'By status' },
  { id: 'last-processed', label: 'Last processed' },
  { id: 'new-accounts', label: 'New accounts by rep' },
]

const SORTS: Record<ReportId, Array<{ value: string; label: string }>> = {
  owners: [
    { value: 'owner_name', label: 'Owner name' },
    { value: 'merchant_count', label: 'Merchant count' },
  ],
  'legal-names': [
    { value: 'legal_name', label: 'Legal name' },
    { value: 'merchant_count', label: 'Merchant count' },
  ],
  statuses: [
    { value: 'dba_name', label: 'DBA' },
    { value: 'legal_name', label: 'Legal name' },
    { value: 'status', label: 'Status' },
    { value: 'created_at', label: 'Boarded date' },
  ],
  'last-processed': [
    { value: 'last_batch_at', label: 'Last batch' },
    { value: 'last_transaction_sync_at', label: 'Last sync' },
    { value: 'dba_name', label: 'DBA' },
    { value: 'status', label: 'Status' },
  ],
  'new-accounts': [
    { value: 'period', label: 'Period' },
    { value: 'rep_code', label: 'Rep code' },
    { value: 'count', label: 'Accounts' },
  ],
}

const DEFAULT_SORT: Record<ReportId, { sort_by: string; sort_dir: 'asc' | 'desc' }> = {
  owners: { sort_by: 'owner_name', sort_dir: 'asc' },
  'legal-names': { sort_by: 'legal_name', sort_dir: 'asc' },
  statuses: { sort_by: 'dba_name', sort_dir: 'asc' },
  'last-processed': { sort_by: 'last_batch_at', sort_dir: 'desc' },
  'new-accounts': { sort_by: 'period', sort_dir: 'desc' },
}

function statusTone(status: string): StatusTone {
  if (status === 'active') return 'success'
  if (status === 'suspended') return 'warning'
  if (status === 'terminated') return 'danger'
  return 'neutral'
}

function formatWhen(value: string | null): string {
  if (!value) return 'No sync recorded'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'No sync recorded'
  return date.toLocaleString()
}

export function ReportsPage() {
  const { canViewReports, hasRole } = usePermissions()
  const allowed = canViewReports()
  const [report, setReport] = useState<ReportId>('owners')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [inactiveDays, setInactiveDays] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [groupBy, setGroupBy] = useState<'month' | 'year'>('month')
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.owners.sort_by)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(DEFAULT_SORT.owners.sort_dir)
  const [exportError, setExportError] = useState<string | null>(null)

  const params: ReportParams = {
    page,
    per_page: 25,
    sort_by: sortBy,
    sort_dir: sortDir,
    search: report === 'new-accounts' ? undefined : search || undefined,
    status: report === 'statuses' || report === 'last-processed' ? status || undefined : undefined,
    inactive_days: report === 'last-processed' && Number(inactiveDays) >= 1 ? Number(inactiveDays) : undefined,
    from: report === 'new-accounts' ? from || undefined : undefined,
    to: report === 'new-accounts' ? to || undefined : undefined,
    group_by: report === 'new-accounts' ? groupBy : undefined,
  }

  const query = useQuery({
    queryKey: ['reports', report, params],
    queryFn: () => fetchReport<OwnerReportRow | LegalNameReportRow | StatusReportRow | LastProcessedReportRow | NewAccountReportRow>(report, params),
    enabled: allowed,
  })

  const download = useMutation({
    mutationFn: () => exportReport(report, { ...params, page: undefined, per_page: undefined }),
    onSuccess: () => setExportError(null),
    onError: () => setExportError('The export could not be downloaded.'),
  })

  const selectReport = (next: ReportId) => {
    setReport(next)
    setPage(1)
    setSearchInput('')
    setSearch('')
    setStatus('')
    setInactiveDays('')
    setFrom('')
    setTo('')
    setGroupBy('month')
    setSortBy(DEFAULT_SORT[next].sort_by)
    setSortDir(DEFAULT_SORT[next].sort_dir)
    setExportError(null)
  }

  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  const data = query.data
  const forbidden = !allowed || (query.isError && apiErrorStatus(query.error) === 403)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        subtitle="Owners, legal names, account status, last batch, and new accounts by rep code."
        action={data?.can_export ? (
          <Button variant="secondary" onClick={() => download.mutate()} isLoading={download.isPending}>
            Export CSV
          </Button>
        ) : undefined}
      />

      <div role="tablist" aria-label="Reports" className="flex flex-wrap gap-2">
        {REPORTS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={report === item.id}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              report === item.id ? 'bg-brand-700 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200'
            }`}
            onClick={() => selectReport(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {exportError ? <p role="alert" className="text-sm text-rose-700">{exportError}</p> : null}
      {forbidden ? <p role="alert" className="text-sm text-rose-700">You don&apos;t have access to reports.</p> : null}

      {allowed && !forbidden ? (
        <SurfaceCard padding="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {report !== 'new-accounts' ? (
              <form onSubmit={submitSearch} className="flex flex-wrap gap-2">
                <Input
                  type="search"
                  aria-label={report === 'owners' || report === 'legal-names' ? 'Search by name' : 'Search by DBA or legal name'}
                  placeholder={report === 'owners' || report === 'legal-names' ? 'Search by name' : 'Search by DBA or legal name'}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="w-64"
                />
                <Button type="submit" variant="secondary">Search</Button>
              </form>
            ) : null}

            {report === 'statuses' || report === 'last-processed' ? (
              <select
                aria-label="Filter by status"
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value)
                  setPage(1)
                }}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
            ) : null}

            {report === 'last-processed' ? (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                No processing in
                <input
                  aria-label="Days without processing"
                  type="number"
                  min={1}
                  max={3650}
                  value={inactiveDays}
                  onChange={(event) => {
                    setInactiveDays(event.target.value)
                    setPage(1)
                  }}
                  className="w-20 rounded-xl border border-slate-200 px-3 py-2"
                />
                days
              </label>
            ) : null}

            {report === 'new-accounts' ? (
              <>
                <label className="text-sm text-slate-700">
                  From
                  <input
                    aria-label="From date"
                    type="date"
                    value={from || (typeof data?.filters.from === 'string' ? data.filters.from : '')}
                    onChange={(event) => {
                      setFrom(event.target.value)
                      setPage(1)
                    }}
                    className="mt-1 block rounded-xl border border-slate-200 px-3 py-2"
                  />
                </label>
                <label className="text-sm text-slate-700">
                  To
                  <input
                    aria-label="To date"
                    type="date"
                    value={to || (typeof data?.filters.to === 'string' ? data.filters.to : '')}
                    onChange={(event) => {
                      setTo(event.target.value)
                      setPage(1)
                    }}
                    className="mt-1 block rounded-xl border border-slate-200 px-3 py-2"
                  />
                </label>
                <select
                  aria-label="Group by period"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={groupBy}
                  onChange={(event) => {
                    setGroupBy(event.target.value === 'year' ? 'year' : 'month')
                    setPage(1)
                  }}
                >
                  <option value="month">By month</option>
                  <option value="year">By year</option>
                </select>
              </>
            ) : null}

            <select
              aria-label="Sort report"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={sortBy}
              onChange={(event) => {
                setSortBy(event.target.value)
                setPage(1)
              }}
            >
              {SORTS[report].map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
                setPage(1)
              }}
            >
              {sortDir === 'asc' ? 'Ascending' : 'Descending'}
            </Button>
          </div>
        </SurfaceCard>
      ) : null}

      {query.isLoading ? <p aria-live="polite" className="text-sm text-slate-500">Loading report...</p> : null}
      {query.isError && !forbidden ? <p role="alert" className="text-sm text-rose-700">Could not load this report.</p> : null}

      {data && report === 'statuses' && data.counts ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {data.counts.map((count) => (
            <div
              key={count.status}
              className={`rounded-2xl border bg-white p-4 ${status === count.status ? 'border-brand-500 ring-2 ring-brand-200' : 'border-slate-200'}`}
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() => {
                  setStatus(count.status)
                  setPage(1)
                }}
              >
                <p className="text-sm capitalize text-slate-500">{count.status}</p>
                <p className="mt-1 text-3xl font-semibold text-slate-950">{count.count}</p>
              </button>
              <Link to={count.merchants_href} className="mt-2 inline-block text-sm text-blue-700 hover:text-blue-900">
                Open merchant list
              </Link>
            </div>
          ))}
        </div>
      ) : null}

      {data && report === 'new-accounts' && data.scope === 'self' && !hasRole('sales_rep') ? (
        <SurfaceCard>
          <p className="text-sm text-slate-600">
            New accounts by rep shows your own row. No rep code is linked to this user, so there is nothing to list.
          </p>
        </SurfaceCard>
      ) : null}

      {data && report === 'new-accounts' && (data.scope === 'portfolio' || hasRole('sales_rep')) ? (
        <p className="text-sm text-slate-500">
          {data.scope === 'self'
            ? 'Counts include only merchants you can already view. Each rep code on those merchants is its own row.'
            : 'Each rep code is its own row. A catalog name is shown when the code is already on file.'}
        </p>
      ) : null}

      {data && !(report === 'new-accounts' && data.scope === 'self' && !hasRole('sales_rep')) ? (
        <ReportTable report={report} data={data} />
      ) : null}

      {data && data.last_page > 1 ? (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>Showing {data.from ?? 0}-{data.to ?? 0} of {data.total}</span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</Button>
            <Button variant="secondary" disabled={page >= data.last_page} onClick={() => setPage((current) => current + 1)}>Next</Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ReportTable({
  report,
  data,
}: {
  report: ReportId
  data: ReportEnvelope<OwnerReportRow | LegalNameReportRow | StatusReportRow | LastProcessedReportRow | NewAccountReportRow>
}) {
  if (data.data.length === 0) {
    return (
      <SurfaceCard>
        <p className="py-8 text-center text-sm text-slate-500">No rows match these filters.</p>
      </SurfaceCard>
    )
  }

  return (
    <SurfaceCard padding="p-0">
      <div className="overflow-x-auto">
        {report === 'owners' ? <OwnerTable rows={data.data as OwnerReportRow[]} /> : null}
        {report === 'legal-names' ? <LegalTable rows={data.data as LegalNameReportRow[]} /> : null}
        {report === 'statuses' ? <StatusTable rows={data.data as StatusReportRow[]} /> : null}
        {report === 'last-processed' ? <LastProcessedTable rows={data.data as LastProcessedReportRow[]} /> : null}
        {report === 'new-accounts' ? <NewAccountTable rows={data.data as NewAccountReportRow[]} /> : null}
      </div>
    </SurfaceCard>
  )
}

function OwnerTable({ rows }: { rows: OwnerReportRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <caption className="sr-only">Merchants by owner</caption>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Owner</th>
          <th className="px-4 py-3 font-medium">Merchants</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.owner_name} className="align-top">
            <td className="px-4 py-3 font-medium text-slate-950">{row.owner_name}</td>
            <td className="px-4 py-3">
              <p className="mb-2 text-xs text-slate-500">{row.merchant_count}</p>
              <ul className="space-y-1">
                {row.merchants.map((merchant) => (
                  <li key={merchant.id}>
                    <Link to={`/merchants/${merchant.id}`} className="text-blue-700 hover:text-blue-900">{merchant.dba_name}</Link>
                    <StatusPill tone={statusTone(merchant.status)} className="ml-2 capitalize">{merchant.status}</StatusPill>
                  </li>
                ))}
              </ul>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LegalTable({ rows }: { rows: LegalNameReportRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <caption className="sr-only">Merchants by legal name</caption>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Legal name</th>
          <th className="px-4 py-3 font-medium">DBAs</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.legal_name ?? 'none'} className="align-top">
            <td className="px-4 py-3 font-medium text-slate-950">{row.legal_name ?? 'No legal name'}</td>
            <td className="px-4 py-3">
              <ul className="space-y-1">
                {row.merchants.map((merchant) => (
                  <li key={merchant.id}>
                    <Link to={`/merchants/${merchant.id}`} className="text-blue-700 hover:text-blue-900">{merchant.dba_name}</Link>
                    <StatusPill tone={statusTone(merchant.status)} className="ml-2 capitalize">{merchant.status}</StatusPill>
                  </li>
                ))}
              </ul>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function StatusTable({ rows }: { rows: StatusReportRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <caption className="sr-only">Merchants by status</caption>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">DBA</th>
          <th className="px-4 py-3 font-medium">Legal name</th>
          <th className="px-4 py-3 font-medium">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="px-4 py-3"><Link to={`/merchants/${row.id}`} className="font-medium text-blue-700 hover:text-blue-900">{row.dba_name}</Link></td>
            <td className="px-4 py-3 text-slate-600">{row.legal_name ?? 'No legal name'}</td>
            <td className="px-4 py-3"><StatusPill tone={statusTone(row.status)} className="capitalize">{row.status}</StatusPill></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function LastProcessedTable({ rows }: { rows: LastProcessedReportRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <caption className="sr-only">Last processed date by merchant</caption>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Merchant</th>
          <th className="px-4 py-3 font-medium">Status</th>
          <th className="px-4 py-3 font-medium">Last batch</th>
          <th className="px-4 py-3 font-medium">Last sync</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="px-4 py-3">
              <Link to={`/merchants/${row.id}`} className="font-medium text-blue-700 hover:text-blue-900">{row.dba_name}</Link>
              <p className="text-xs text-slate-500">{row.legal_name ?? 'No legal name'}</p>
            </td>
            <td className="px-4 py-3"><StatusPill tone={statusTone(row.status)} className="capitalize">{row.status}</StatusPill></td>
            <td className="px-4 py-3 text-slate-700">
              {row.last_batch_state === 'not_synced' ? 'Not synced' : formatWhen(row.last_batch_at)}
            </td>
            <td className="px-4 py-3 text-slate-600">{formatWhen(row.last_transaction_sync_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function NewAccountTable({ rows }: { rows: NewAccountReportRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <caption className="sr-only">New accounts by rep code</caption>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-4 py-3 font-medium">Period</th>
          <th className="px-4 py-3 font-medium">Rep code</th>
          <th className="px-4 py-3 font-medium">Catalog name</th>
          <th className="px-4 py-3 font-medium">Accounts</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={`${row.period}-${row.rep_code ?? 'none'}`}>
            <td className="px-4 py-3">{row.period}</td>
            <td className="px-4 py-3 font-mono text-xs">{row.rep_code ?? 'Unassigned'}</td>
            <td className="px-4 py-3">{row.user?.name ?? row.rep_name ?? '—'}</td>
            <td className="px-4 py-3">{row.merchant_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
